import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { noop } from 'lodash';
import useIsSaving from 'containers/Survey/hooks/useIsSaving';
import {
  setAiPanelState as setAiPanelStateAction,
  updateAiMessage as updateAiMessageAction,
  setAiStatus as setAiStatusAction,
  startStreamingAiMessage as startStreamingAiMessageAction,
  updateStreamingAiMessageContent as updateStreamingAiMessageContentAction,
  setErrorStreamingMessage as setErrorStreamingMessageAction,
  resetStreamingMessage as resetStreamingMessageAction,
  setAiUserPrompt as setAiUserPromptAction,
  setAiUserPromptWithOptions as setAiUserPromptWithOptionsAction,
  clearStreamingMessageContent as clearStreamingMessageContentAction,
} from './actions';

import {
  makeSelectAIChatHistory,
  makeSelectAIisFetching,
  makeSelectAIisError,
  makeSelectAIisOpen,
  makeSelectAiStatus,
  makeSelectStreamingMessage,
} from './selectors';

export default function useAiSurveyBuilder(canShowAiBuilder = true) {
  const dispatch = useDispatch();
  const discussion = useSelector(makeSelectAIChatHistory);
  const isFetching = useSelector(makeSelectAIisFetching);
  const isError = useSelector(makeSelectAIisError);
  const isOpen = useSelector(makeSelectAIisOpen);
  const streamStatus = useSelector(makeSelectAiStatus);
  const isSurveySaving = useIsSaving();
  const streamingMessage = useSelector(makeSelectStreamingMessage);
  const setAiStatus = useCallback(
    (newStatus) => dispatch(setAiStatusAction(newStatus)),
    [dispatch],
  );
  const startStreamingMessage = useCallback(
    (prompt) => {
      dispatch(startStreamingAiMessageAction(prompt));
    },
    [dispatch],
  );
  const updateStreamingMessageContent = useCallback(
    (message) => dispatch(updateStreamingAiMessageContentAction(message)),
    [dispatch],
  );
  const setErrorStreamingMessage = useCallback(
    (message) => dispatch(setErrorStreamingMessageAction(message)),
    [dispatch],
  );
  const resetStreamingMessage = useCallback(
    () => dispatch(resetStreamingMessageAction()),
    [dispatch],
  );
  const clearStreamingMessageContent = useCallback(
    () => dispatch(clearStreamingMessageContentAction()),
    [dispatch],
  );
  // ///
  const updateMessage = useCallback(
    (messageUpdates) => {
      dispatch(updateAiMessageAction(messageUpdates));
    },
    [dispatch],
  );
  const setPanelState = useCallback(
    (state) => dispatch(setAiPanelStateAction(state)),
    [dispatch],
  );
  const setUserPrompt = useCallback(
    (message) => {
      setPanelState(true);
      dispatch(setAiUserPromptAction(message));
    },
    [dispatch, setPanelState],
  );
  const setUserPromptWithOptions = useCallback(
    (message, messageWithOptionPlaceholder, options) => {
      setPanelState(true);
      dispatch(
        setAiUserPromptWithOptionsAction({
          message,
          messageWithOptionPlaceholder,
          options,
        }),
      );
    },
    [dispatch, setPanelState],
  );
  const startStreamingMessageWithOption = useCallback(
    (prompt, option) => {
      if (option) {
        if (option === '...') {
          const constructedMessage = prompt.replaceAll('{{option}}', '');
          setUserPrompt(constructedMessage);
        } else {
          const constructedMessage = prompt.replaceAll('{{option}}', option);
          startStreamingMessage(constructedMessage);
        }
      } else {
        setUserPrompt(prompt);
      }
    },
    [startStreamingMessage, setUserPrompt],
  );

  const toggleOpen = useCallback(
    (forcedState) => {
      if (typeof forcedState === 'boolean') {
        setPanelState(forcedState);
      } else {
        setPanelState(!isOpen);
      }
    },
    [setPanelState, isOpen],
  );
  if (!canShowAiBuilder) {
    return {
      updateMessage: noop,
      toggleOpen: noop,
      discussion: [],
      isFetching: false,
      isError: false,
      isOpen: false,
      streamStatus,
      streamingMessage: null,
      startStreamingMessage: noop,
      startStreamingMessageWithOption: noop,
      setUserPrompt: noop,
      setUserPromptWithOptions: noop,
      //
      setAiStatus: noop,
      updateStreamingMessageContent: noop,
      setErrorStreamingMessage: noop,
      resetStreamingMessage: noop,
      clearStreamingMessageContent: noop,
      //
      isSurveySaving,
    };
  }
  return {
    updateMessage,
    toggleOpen,
    discussion,
    isFetching,
    isError,
    isOpen,
    streamStatus,
    streamingMessage,
    startStreamingMessage,
    startStreamingMessageWithOption,
    setUserPrompt,
    setUserPromptWithOptions,
    //
    setAiStatus,
    updateStreamingMessageContent,
    setErrorStreamingMessage,
    resetStreamingMessage,
    clearStreamingMessageContent,
    //
    isSurveySaving,
  };
}
